// How to play each role, in as few words as will do. Matt, on the Hall's
// old opening paragraph: "this first paragraph isn't helpful... All of this
// needs to be made much more concise. Because it's way too much text. No
// one's going to read this."
//
// So: the intro is one sentence about what you actually do with your hands.
// Every line after it is a thing you can do, said in under a dozen words.
// Shown on the phone the first time a person holds the role, and any time
// after from the ? in the header.
//
// Every role: an Undo button appears after an action that showed nothing
// new (not a hunt, a fight, an experiment, a march). It takes back that one
// action until you press Done.

export const GUIDES = {
  // Playtest 9 cut these to a reference card ("at a glance"): the tutorial
  // teaches, this reminds. Seven lines of day at most, plain words only.
  culture: {
    title: 'The Hall at a glance',
    intro: 'You lead the town: play cards from your hand onto your three counselors. Each worker you are given at dawn is two cards.',
    day: [
      'Your hand is dealt when the day starts: 2 cards per worker.',
      'Tap a card, then a counselor. It is free, and does what it says.',
      'Each counselor likes one kind of card (it says so on the chair). Play them one and they add a bonus.',
      'Each counselor takes one card a day: three plays, whatever the hand.',
      'Buy one card a day from the shelf. Its tag is its whole price; it joins your deck.',
      'Trash a card (once a day, a coin): it leaves your deck for good.',
      'A grievance gives nothing: play it to settle it, or it costs 1 happiness a night in hand.',
      'Counselors retire in time. Pick a replacement from two.',
    ],
    gives: [
      'Happiness. Under 3, half the town works; at 0, you lose.',
      'Extra workers for any role, and new people.',
    ],
    needs: [
      'Goods and coin from the Stewards.',
      'Technologies from the Scholars.',
    ],
    words: [
      ['Treaty', 'From the Bronze Age: a friendly people becomes an ally. Carts twice as full, no raids.'],
      ['Counselor', 'One of your three seats. Each takes one card a day, and likes one kind of card.'],
      ['Burden', 'The dark card on the shelf. It pays you to take it, then sits in your deck as a grievance.'],
      ['Pairs up', 'A card that gives more when another of its kind was played earlier today.'],
      ['Worker', 'Two cards, dealt when the day starts. Ask for more at dawn to see more of your deck.'],
      ['Buy', 'One card a day from the shelf.'],
      ['Trash', 'Gone from your deck for good.'],
      ['Grievance', 'A bad card in your deck. Play it to settle it (it gives nothing), trash it for a coin, or lose 1 happiness a night while it sits in hand.'],
    ],
  },
  trade: {
    title: 'The Stewards at a glance',
    intro: 'You run the city. Each worker is two shifts; each shift goes into a building.',
    day: [
      'Tap a building with a gold outline to put a shift in it.',
      'Build: two shifts plus materials. The menu groups every building; locked ones say which technology they need.',
      'Houses: the town only grows while it has homes. Each House holds 2 more people.',
      'Take in land: tap it, pick a blue hex, tap again.',
      'Train units for the Frontier.',
      'Caravans feed villages; grain turns coin into food.',
      'Stores past the granary and warehouse caps spoil overnight.',
    ],
    gives: [
      'Food: the town eats half its people each night.',
      'Goods and coin, which pay for almost everything.',
    ],
    needs: [
      'The Frontier to keep raiders off the city.',
      'Technologies from the Scholars for new buildings, and the Scholars\' machines.',
    ],
    words: [
      ['Upgrade', 'From the Bronze Age: a workshop becomes a foundry, a field an orchard. One more a shift.'],
      ['City', 'A town of 3 with ground of its own becomes a city: a focus, a district, size to 6.'],
      ['Shift', 'Half a worker\'s day.'],
      ['Plot', 'A sixth of a hex. Big buildings take two.'],
      ['Burned', 'Hit by raiders. Makes nothing until repaired.'],
      ['Homes', 'How many people the town can hold: 2 for each House. The city starts with 3.'],
    ],
  },
  science: {
    title: 'The Scholars at a glance',
    intro: 'Mix principles two at a time, work out what each is made of, then invent technologies.',
    day: [
      'Every principle is hot or cold, wet or dry, heavy or light. No two alike.',
      'Fill both flasks, then Mix (a worker and a good). The glass says what they share.',
      'Mark your notebook: ✕ can\'t be, ● it is.',
      'To invent one: pick a technology first, then mix two that fit it.',
      'Publish a theory once you are sure of a nature: +2 knowledge if it holds.',
      'Past tier 2, technologies are made in MACHINES the Stewards build: the Kiln, the Still, the Crucible.',
      'Feed a machine two things, in order; one comes out. Its hidden law says how: crack it.',
      'A technology names what it needs. When the shelf has it, Make it.',
      'Study and Ponder make knowledge, which pays for technologies.',
    ],
    gives: [
      'Technologies, which change everyone\'s board.',
      'A held theory teaches a role: +1 worker at the next dawn.',
    ],
    needs: [
      'The Frontier finds land, and each new region tells you something.',
      'The Stewards build libraries (more cheap mixes) and your machines.',
      'Goods: every mix and every firing uses some.',
    ],
    words: [
      ['Survey', 'From the Bronze Age: map the nearest far region from the city (2 knowledge).'],
      ['Principle', 'Fire, Water, Stone, Wind, Growth, Metal, Number, Word.'],
      ['Nature', 'What a principle is made of: three properties.'],
      ['Research', 'Picking a technology before you mix.'],
      ['Machine', 'Kiln, Still, Crucible: two things in, one out, by its law.'],
      ['Law', 'One rule per property, like "hot if either one in is hot".'],
      ['Shelf', 'What your machines have made: glazes, essences, alloys.'],
    ],
  },
  military: {
    title: 'The Frontier at a glance',
    intro: 'You command units on the map. Each worker is two actions: a unit stepping, hunting or fighting.',
    day: [
      'Tap a gold hex to step: 1 action, 2 into rough ground.',
      'Press and hold any hex to see what it is.',
      'Tap a camp or a people beside you: fight or parley.',
      'A fight: both roll; each 4 or more takes one off the other.',
      'A pioneer founds a village where it stands, and is spent.',
      'Any unit can work the land it stands on for food or goods.',
      'No units out? Hunt near the walls: an action for a die of food.',
    ],
    gives: [
      'New land: clues, finds and room.',
      'Safety: camps send raiders until they are cleared.',
    ],
    needs: [
      'The Stewards to train units.',
      'Technologies for stronger units.',
    ],
    words: [
      ['Fort', 'From the Bronze Age: a warband raises a fort where it stands. Raiders cannot pass it.'],
      ['Order', 'One worker: one unit\'s turn.'],
      ['Strength', 'A unit\'s size, dice and health in one number.'],
      ['Parley', 'Pay coin and roll: on a 3+ a camp breaks up or a people warms to you.'],
      ['Merge', 'Fold a warband into one beside it. No splitting after.'],
    ],
  },
};

export const GUIDE_SEEN_KEY = (role) => `commonwealth:guide-seen:${role}`;
